import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CatalogService,
  HospitalityBookingBoardEntry,
  HospitalityBookingBoardReservation,
  HospitalityProperty
} from './catalog.service';

type CalendarBlock = {
  label: string;
  status: string;
  startUtc: string;
  endUtc: string;
  link: string;
  kind: 'assigned' | 'unassigned';
};

@Component({
  standalone: true,
  selector: 'ain-hospitality-booking-board',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, SelectModule],
  template: `
    <ain-page [title]="t('catalog.hospitality.bookingBoard.title')" [description]="t('catalog.hospitality.bookingBoard.description')">
      @if (loading() && rows().length === 0 && !selectedPropertyId) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <p-select
              [options]="propertyOptions()"
              [(ngModel)]="selectedPropertyId"
              (ngModelChange)="load()"
              optionLabel="label"
              optionValue="value"
              [placeholder]="t('catalog.hospitality.property')"
              [filter]="true"
              appendTo="body" />
            <button pButton type="button" icon="pi pi-chevron-left" severity="secondary" (click)="shiftWindow(-1)"></button>
            <button pButton type="button" [label]="t('catalog.rental.calendar.today')" severity="secondary" (click)="goToday()"></button>
            <button pButton type="button" icon="pi pi-chevron-right" severity="secondary" (click)="shiftWindow(1)"></button>
            <span class="window-label">{{ windowLabel() }}</span>
            <p-select [options]="windowOptions" [(ngModel)]="windowDays" (ngModelChange)="load()" optionLabel="label" optionValue="value" appendTo="body" />
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
            <a routerLink="/hospitality/reservations">{{ t('catalog.hospitality.reservations.title') }}</a>
          </div>

          <div class="legend">
            <span class="chip assigned">{{ t('catalog.hospitality.bookingBoard.legendAssigned') }}</span>
            <span class="chip unassigned">{{ t('catalog.hospitality.bookingBoard.legendUnassigned') }}</span>
          </div>

          @if (!selectedPropertyId) {
            <ain-state type="empty" [title]="t('catalog.hospitality.bookingBoard.selectProperty')" />
          } @else if (loading()) {
            <ain-state type="loading" [title]="t('catalog.loading')" />
          } @else if (rows().length === 0) {
            <ain-state type="empty" [title]="t('catalog.hospitality.bookingBoard.noRooms')" />
          } @else {
            <div class="calendar-scroll">
              <div class="calendar-grid" [style.--day-count]="dayHeaders().length">
                <div class="corner">{{ t('catalog.hospitality.bookingBoard.roomColumn') }}</div>
                @for (day of dayHeaders(); track day.key) {
                  <div class="day-head" [class.weekend]="day.isWeekend">
                    <strong>{{ day.label }}</strong>
                    <small>{{ day.subLabel }}</small>
                  </div>
                }

                @for (row of rows(); track row.rowId + row.rowKind) {
                  <div class="unit-label" [class.unassigned]="row.rowKind === 'Unassigned'">
                    <strong>{{ row.rowLabel }}</strong>
                    <small>
                      @if (row.rowKind === 'Room') {
                        {{ row.rowStatus }}
                      } @else {
                        {{ t('catalog.hospitality.bookingBoard.unassignedRow') }}
                      }
                    </small>
                  </div>
                  <div class="unit-track">
                    <div class="day-grid">
                      @for (day of dayHeaders(); track day.key) {
                        <div class="day-cell" [class.weekend]="day.isWeekend"></div>
                      }
                      @for (block of blocksForRow(row); track block.label + block.startUtc) {
                        <a
                          class="block"
                          [class.assigned]="block.kind === 'assigned'"
                          [class.unassigned]="block.kind === 'unassigned'"
                          [style.left.%]="blockLeftPercent(block)"
                          [style.width.%]="blockWidthPercent(block)"
                          [routerLink]="block.link"
                          [title]="block.label + ' · ' + block.status">
                          {{ block.label }}
                        </a>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 12px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .toolbar a { color: #1d4ed8; font-weight: 600; text-decoration: none; margin-inline-start: auto; }
    .window-label { font-weight: 600; color: #334155; min-width: 180px; }
    .legend { display: flex; gap: 8px; flex-wrap: wrap; }
    .chip { font-size: 12px; padding: 4px 10px; border-radius: 999px; font-weight: 600; }
    .chip.assigned { background: #dbeafe; color: #1d4ed8; }
    .chip.unassigned { background: #fef3c7; color: #b45309; }
    .calendar-scroll { overflow-x: auto; border: 1px solid #e2e8f0; border-radius: 12px; }
    .calendar-grid {
      display: grid;
      grid-template-columns: 220px repeat(var(--day-count), minmax(56px, 1fr));
      min-width: max(100%, calc(220px + var(--day-count) * 56px));
    }
    .corner, .day-head, .unit-label {
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
      padding: 8px;
    }
    .corner { font-weight: 600; font-size: 12px; color: #475569; }
    .day-head { text-align: center; display: grid; gap: 2px; font-size: 11px; }
    .day-head.weekend { background: #f1f5f9; }
    .unit-label { display: grid; gap: 2px; border-bottom: 1px solid #eef2f7; }
    .unit-label.unassigned { background: #fffbeb; }
    .unit-label strong { font-size: 13px; }
    .unit-label small { color: #64748b; font-size: 11px; }
    .unit-track { border-bottom: 1px solid #eef2f7; min-height: 56px; position: relative; grid-column: 2 / -1; }
    .day-grid { display: grid; grid-template-columns: repeat(var(--day-count), minmax(56px, 1fr)); height: 100%; position: relative; }
    .day-cell { border-inline-end: 1px solid #f1f5f9; min-height: 56px; }
    .day-cell.weekend { background: #fafafa; }
    .block {
      position: absolute;
      top: 8px;
      height: 40px;
      border-radius: 8px;
      padding: 4px 8px;
      font-size: 11px;
      font-weight: 600;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      color: #0f172a;
      text-decoration: none;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
    }
    .block.assigned { background: #bfdbfe; border: 1px solid #93c5fd; }
    .block.unassigned { background: #fde68a; border: 1px solid #fbbf24; }
  `]
})
export class HospitalityBookingBoardComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(false);
  readonly rows = signal<HospitalityBookingBoardEntry[]>([]);
  readonly properties = signal<HospitalityProperty[]>([]);
  readonly windowStart = signal(this.startOfDay(new Date()));

  selectedPropertyId = '';
  windowDays = 14;

  readonly windowOptions = [
    { label: '7', value: 7 },
    { label: '14', value: 14 },
    { label: '30', value: 30 }
  ];

  readonly propertyOptions = computed(() =>
    this.properties().map(item => ({
      value: item.id,
      label: item.propertyCode ? `${item.propertyCode} — ${item.name}` : item.name
    })));

  readonly dayHeaders = computed(() => {
    const start = this.windowStart();
    return Array.from({ length: this.windowDays }, (_, index) => {
      const date = this.addDays(start, index);
      const day = date.getDay();
      return {
        key: date.toISOString().slice(0, 10),
        label: date.toLocaleDateString(undefined, { weekday: 'short' }),
        subLabel: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        isWeekend: day === 0 || day === 6,
        date
      };
    });
  });

  readonly windowLabel = computed(() => {
    const days = this.dayHeaders();
    if (days.length === 0) return '';
    const first = days[0].date;
    const last = days[days.length - 1].date;
    return `${first.toLocaleDateString()} – ${last.toLocaleDateString()}`;
  });

  private readonly windowEnd = computed(() => this.addDays(this.windowStart(), this.windowDays));

  ngOnInit(): void {
    this.service.listHospitalityProperties().subscribe({
      next: rows => {
        this.properties.set(rows);
        if (rows.length === 1) {
          this.selectedPropertyId = rows[0].id;
          this.load();
        }
      }
    });
  }

  load(): void {
    if (!this.selectedPropertyId) {
      this.rows.set([]);
      return;
    }

    this.loading.set(true);
    this.service.getHospitalityBookingBoard(
      this.selectedPropertyId,
      this.windowStart().toISOString(),
      this.windowEnd().toISOString()
    ).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  shiftWindow(direction: -1 | 1): void {
    this.windowStart.update(value => this.addDays(value, direction * this.windowDays));
    this.load();
  }

  goToday(): void {
    this.windowStart.set(this.startOfDay(new Date()));
    this.load();
  }

  blocksForRow(row: HospitalityBookingBoardEntry): CalendarBlock[] {
    const kind = row.rowKind === 'Unassigned' ? 'unassigned' : 'assigned';
    return row.reservations.map(item => this.toBlock(item, kind));
  }

  blockLeftPercent(block: CalendarBlock): number {
    const windowStartMs = this.windowStart().getTime();
    const windowEndMs = this.windowEnd().getTime();
    const startMs = Math.max(new Date(block.startUtc).getTime(), windowStartMs);
    const span = windowEndMs - windowStartMs;
    if (span <= 0) return 0;
    return ((startMs - windowStartMs) / span) * 100;
  }

  blockWidthPercent(block: CalendarBlock): number {
    const windowStartMs = this.windowStart().getTime();
    const windowEndMs = this.windowEnd().getTime();
    const startMs = Math.max(new Date(block.startUtc).getTime(), windowStartMs);
    const endMs = Math.min(new Date(block.endUtc).getTime(), windowEndMs);
    const span = windowEndMs - windowStartMs;
    if (span <= 0 || endMs <= startMs) return 0;
    return ((endMs - startMs) / span) * 100;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private toBlock(reservation: HospitalityBookingBoardReservation, kind: 'assigned' | 'unassigned'): CalendarBlock {
    return {
      kind,
      label: reservation.reservationReference,
      status: reservation.status,
      startUtc: reservation.checkInDateUtc,
      endUtc: reservation.checkOutDateUtc,
      link: `/hospitality/reservations/${reservation.id}`
    };
  }

  private startOfDay(value: Date): Date {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private addDays(value: Date, days: number): Date {
    const date = new Date(value);
    date.setDate(date.getDate() + days);
    return date;
  }
}
